import { z } from 'zod';
import { FULFILLMENT_METHODS, ORDER_CHANNELS, ORDER_STATUSES, PRODUCT_TYPES, } from '../constants/order-states.js';
import { moneyCentsSchema, uuidSchema } from './common.js';
import { addressSchema } from './customer.js';
import { repairDateSchema } from './repair.js';
/**
 * t1137: "this line's unit may not be the one recorded" — the IMEIs / ICCIDs /
 * SKUs it might really be, and/or the batch it came out of. Everything is
 * optional: a bare flag is a valid flag.
 */
export const flagOrderItemIdentitySchema = z.object({
    candidates: z
        .array(z.string().trim().min(1).max(64))
        .max(100)
        .default([])
        .transform((v) => [...new Set(v)]),
    pool: z.string().trim().max(500).nullable().default(null),
    note: z.string().trim().max(1000).nullable().default(null),
});
export const resolveOrderItemIdentitySchema = z.object({
    /** The IMEI / ICCID the item really was. Null/absent = the recorded unit was right. */
    actualIdentifier: z.string().trim().min(1).max(64).nullable().default(null),
    note: z.string().trim().max(1000).nullable().default(null),
});
export const orderItemIdentityFlagSchema = z.object({
    candidates: z.array(z.string()),
    pool: z.string().nullable(),
    note: z.string().nullable(),
    flaggedAt: z.iso.datetime(),
    flaggedByLabel: z.string().nullable(),
    resolvedAt: z.iso.datetime().nullable(),
    resolvedByLabel: z.string().nullable(),
    resolutionNote: z.string().nullable(),
});
/** t1375: how a sold plan is set up at purchase — carried on the plan's order
 *  line and copied onto the subscription once the order is paid. */
export const planSetupSchema = z.object({
    label: z.string().trim().max(80).nullable().optional(),
    /** The SIM's number, typed or copied from the inventory unit picked. */
    simIccid: z.string().trim().min(1).max(40),
    /** The inventory SIM being sold on a `sim` line of this same order, if one was picked. */
    simUnitId: uuidSchema.nullable().optional(),
    provider: z.string().trim().max(60).nullable().optional(),
    device: z.string().trim().max(120).nullable().optional(),
    activatedAt: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-11-05')
        .nullable()
        .optional(),
    costCents: moneyCentsSchema.nullable().optional(),
});
export const orderItemInputSchema = z
    .object({
    /** Staff only (t1137): flag this line's unit as possibly wrong. Ignored for other actors. */
    identityFlag: flagOrderItemIdentitySchema.nullable().optional(),
    productId: uuidSchema,
    qty: z.number().int().positive().max(999),
    /** Required for serialized products — which physical phone is being sold. */
    serializedUnitId: uuidSchema.nullable().default(null),
    /** Staff override; ignored unless the caller has orders:write. */
    unitPriceCents: moneyCentsSchema.nullable().default(null),
    /**
     * Set when this line is being sourced from a location other than the
     * order's own — a counter sale pulling a phone from another branch rather
     * than turning the sale away. Null (the default) means "the order's
     * location", same as before this existed.
     */
    sourceLocationId: uuidSchema.nullable().default(null),
    /** Only meaningful for a service line: the day the customer will bring the device in. */
    dropOffDate: repairDateSchema.nullable().default(null),
    /** Only meaningful for a plan line: the phone line the plan is for. */
    phoneLine: z.string().trim().max(30).nullable().optional(),
    /** Only meaningful for a plan line (t1375): the SIM and the rest of the plan's setup. */
    planSetup: planSetupSchema.nullable().optional(),
    /** Staff only (t1365): this service line bills logged labour on an existing
     *  repair ticket, so no new ticket is opened. Ignored for other actors. */
    repairTicketId: uuidSchema.nullable().optional(),
})
    .refine((v) => v.serializedUnitId === null || v.qty === 1, {
    message: 'A serialized unit is a single physical device — quantity must be 1',
    path: ['qty'],
})
    .refine((v) => !v.phoneLine || v.qty === 1, {
    message: 'A phone line names one line of service — quantity must be 1',
    path: ['qty'],
})
    .refine((v) => !v.planSetup || v.qty === 1, {
    message: 'A plan set up with its own SIM is one line of service — quantity must be 1',
    path: ['qty'],
});
export const createOrderSchema = z
    .object({
    channel: z.enum(ORDER_CHANNELS),
    customerId: uuidSchema.nullable().default(null), // null = POS walk-in
    items: z.array(orderItemInputSchema).min(1).max(100),
    /** Null only for a plan-only ('none') order — nothing physical is booked
     *  against a location. Every other channel still needs one. */
    locationId: uuidSchema.nullable(),
    /** Counter collection unless stated otherwise — the POS default. */
    fulfillmentMethod: z.enum(FULFILLMENT_METHODS).default('pickup'),
    shippingAddress: addressSchema.nullable().default(null),
    shippingCents: moneyCentsSchema.default(0),
    // No taxCents here (t0947): tax is computed server-side, per line, from
    // each item's tax category and the customer's exempt status — a caller
    // naming a figure would only ever be overridden.
    note: z.string().trim().max(1000).nullable().default(null),
    /** Where a repair line's device will be handed over, when it differs
     *  from the order's own location (a shipped order still needs a counter
     *  for the device itself). Null falls back to `locationId`. */
    repairDropOffLocationId: uuidSchema.nullable().default(null),
})
    .refine((v) => v.fulfillmentMethod !== 'shipping' || v.shippingAddress !== null, {
    message: 'A shipped order needs a delivery address',
    path: ['shippingAddress'],
})
    .refine((v) => v.fulfillmentMethod === 'none' || v.locationId !== null, {
    message: 'Choose which location this order is booked against',
    path: ['locationId'],
});
export const transitionOrderSchema = z
    .object({
    to: z.enum(ORDER_STATUSES),
    note: z.string().trim().max(500).nullable().default(null),
    /** Required when moving to `paid` outside of a Stripe webhook. */
    payment: z
        .object({
        provider: z.enum(['cash', 'stripe', 'external_card']),
        amountCents: moneyCentsSchema,
        reference: z.string().trim().max(120).nullable().default(null),
    })
        .nullable()
        .default(null),
    /** Carrier reference; only meaningful when marking the order shipped. */
    trackingNumber: z.string().trim().min(1).max(120).nullable().default(null),
})
    .refine((v) => v.trackingNumber === null || v.to === 'shipped', {
    message: 'A tracking number can only be recorded when marking the order shipped',
    path: ['trackingNumber'],
});
export const orderItemSchema = z.object({
    id: uuidSchema,
    productId: uuidSchema,
    productName: z.string(),
    sku: z.string(),
    qty: z.number().int(),
    serializedUnitId: uuidSchema.nullable(),
    imei: z.string().nullable(),
    /** So the UI can label the identifier IMEI / ICCID / SKU and know what can be flagged (t1137). */
    productType: z.enum(PRODUCT_TYPES),
    identityFlag: orderItemIdentityFlagSchema.nullable(),
    unitPriceCents: z.number().int(),
    taxCents: z.number().int(),
    lineTotalCents: z.number().int(),
    /** Set only when this line was sourced from a location other than the order's own. */
    sourceLocationId: uuidSchema.nullable(),
    sourceLocationName: z.string().nullable(),
    /** How much of `qty` has already come back through an item-level return
     *  (t0946). `qty - returnedQty` is what's left to return. */
    returnedQty: z.number().int(),
});
/**
 * One line staff is returning (t0946): which order item, how much of it, and
 * the restocking percentage to charge on it — in basis points so the API
 * never rounds a percentage input itself, the client does that once and
 * sends a whole number both sides agree on.
 */
export const createOrderReturnItemSchema = z.object({
    orderItemId: uuidSchema,
    qty: z.number().int().positive(),
    restockingFeeBps: z.number().int().min(0).max(10_000),
});
export const createOrderReturnSchema = z
    .object({
    items: z.array(createOrderReturnItemSchema).min(1).max(100),
    note: z.string().trim().max(500).nullable().default(null),
    /** Set by the admin dialog, one per open — a double-submitted return
     *  (a slow network, a doubled click) replays onto the same row instead
     *  of refunding and restocking twice. */
    idempotencyKey: uuidSchema.nullable().default(null),
})
    .refine((v) => new Set(v.items.map((i) => i.orderItemId)).size === v.items.length, {
    message: 'Each line can only appear once in a return',
    path: ['items'],
});
export const orderReturnItemSchema = z.object({
    orderItemId: uuidSchema,
    qty: z.number().int(),
    restockingFeeBps: z.number().int(),
    feeCents: z.number().int(),
    refundCents: z.number().int(),
});
export const orderReturnSchema = z.object({
    id: uuidSchema,
    refundCents: z.number().int(),
    feeCents: z.number().int(),
    note: z.string().nullable(),
    actorLabel: z.string().nullable(),
    items: z.array(orderReturnItemSchema),
    createdAt: z.iso.datetime(),
});
export const createOrderReturnResultSchema = z.object({
    id: uuidSchema,
    refundCents: z.number().int(),
    feeCents: z.number().int(),
    orderStatus: z.enum(ORDER_STATUSES),
    /** How the refund was actually returned, so staff know whether — and how
     *  much — to hand back at the counter versus what went back to a card. */
    byProvider: z.array(z.object({
        // The full set an order's own `payments` row can carry — including
        // `check`/`zelle`, which only the combined purchase-and-rental
        // checkout (t0943) ever records against an order.
        // `gift_card`/`store_credit` (t1102) go back to the card or the account
        // by themselves — nothing for staff to hand over.
        provider: z.enum([
            'cash',
            'stripe',
            'external_card',
            'check',
            'zelle',
            'gift_card',
            'store_credit',
        ]),
        refundCents: z.number().int(),
    })),
});
export const orderSchema = z.object({
    id: uuidSchema,
    orderNumber: z.string(),
    channel: z.enum(ORDER_CHANNELS),
    status: z.enum(ORDER_STATUSES),
    fulfillmentMethod: z.enum(FULFILLMENT_METHODS),
    customerId: uuidSchema.nullable(),
    /** Falls back to the kiosk-captured buyer name/phone below when there's no
     *  linked account — a walk-up purchase still has someone to reach. */
    customerName: z.string().nullable(),
    customerPhone: z.string().nullable(),
    /** Captured directly at kiosk checkout, independent of any account.
     *  Non-null only for kiosk orders; use these (not `customerName`/
     *  `customerPhone`) to tell "no account, but we have contact info" apart
     *  from "fully anonymous". */
    buyerName: z.string().nullable(),
    buyerPhone: z.string().nullable(),
    locationId: uuidSchema.nullable(),
    items: z.array(orderItemSchema),
    subtotalCents: z.number().int(),
    taxCents: z.number().int(),
    /** Whether the customer's tax-exempt status was applied to this order —
     *  kept even when it made no arithmetic difference, so the exemption
     *  itself stays visible on the order. */
    taxExemptApplied: z.boolean(),
    shippingCents: z.number().int(),
    /** Promo-code discount already taken off `totalCents` (0 when none). */
    discountCents: z.number().int(),
    couponCode: z.string().nullable(),
    totalCents: z.number().int(),
    paidCents: z.number().int(),
    refundedCents: z.number().int(),
    balanceDueCents: z.number().int(),
    /** Gift card / store credit held or spent on this order (t1102). Only the
     *  single-order endpoint fills it in. */
    credits: z
        .array(z.object({
        provider: z.enum(['gift_card', 'store_credit']),
        label: z.string(),
        amountCents: z.number().int(),
        /** `pending` = held for an order not yet paid. */
        status: z.string(),
    }))
        .optional(),
    /** Gift cards this order sold (t1102). Only the single-order endpoint fills it in. */
    giftCards: z
        .array(z.object({
        id: uuidSchema,
        codeLast4: z.string(),
        kind: z.enum(['digital', 'physical']),
        status: z.enum(['pending', 'active', 'void']),
        initialCents: z.number().int(),
        /** True while the full code can still be shown to staff. */
        codeAvailable: z.boolean(),
    }))
        .optional(),
    shippingAddress: addressSchema.nullable(),
    trackingNumber: z.string().nullable(),
    note: z.string().nullable(),
    /** Every item-level return (t0946) taken against this order, newest first. */
    returns: z.array(orderReturnSchema),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const orderListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(200).optional(),
    /** 'except_cancelled' matches every status but cancelled (t1056). */
    status: z.enum([...ORDER_STATUSES, 'except_cancelled']).optional(),
    /** Leave out orders with any refund, full or partial (t1334) — the dashboard card; the Orders page never sets it. */
    excludeRefunded: z.stringbool().optional(),
    channel: z.enum(ORDER_CHANNELS).optional(),
    customerId: uuidSchema.optional(),
    /** 'open' = only orders with an unresolved unverified-unit flag (t1137). */
    identityFlag: z.enum(['open']).optional(),
    sort: z.enum(['createdAt', 'totalCents', 'orderNumber']).default('createdAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
export const orderEventSchema = z.object({
    id: uuidSchema,
    fromStatus: z.enum(ORDER_STATUSES).nullable(),
    toStatus: z.enum(ORDER_STATUSES),
    actorType: z.enum(['staff', 'customer', 'kiosk', 'system']),
    actorLabel: z.string().nullable(),
    note: z.string().nullable(),
    /** Set on the event that took a saved-card payment from a linked family
     *  member's card (t1117) — whose card, so history shows who paid. */
    payment: z
        .object({
        cardOwnerName: z.string(),
        cardLast4: z.string().nullable(),
    })
        .nullable(),
    createdAt: z.iso.datetime(),
});
//# sourceMappingURL=order.js.map